import { omit } from "lodash";

import AgentPriority from "constants/agent-priority";
import Role from "constants/role";
import { IAgent, IBatchCreateAgentsPayload } from "store/entities/agents";

import { IKeyAny } from "../api-client";
import { BaseApi } from "../base";
import { IWorkSchedulerBody } from "../interfaces";

/* eslint-disable camelcase */
export interface IAgentBody {
  avatar?: string;
  groups?: number[];
  job_title?: string;
  name?: string;
  max_chats_count?: number;
  work_scheduler?: IWorkSchedulerBody;
  default_group_priority?: AgentPriority;
  permission?: Role;
}
/* eslint-enable */

type IUpdateAgentPayload = Omit<Partial<IAgent>, "settings"> & {
  transactionID?: string;
  settings?: Partial<IAgent["settings"]>;
};

const permissionToRoleMap: { [key in Role]: string } = {
  [Role.Owner]: "owner",
  [Role.Normal]: "normal",
  [Role.Viewer]: "viewer",
};

function mapPermissionToRole(permission: Role): string {
  return permissionToRoleMap[permission] || "normal";
}

function parseBatchCreateAgentsParams(payload: IBatchCreateAgentsPayload): IKeyAny {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return payload.agents.map((agent: any) => ({
    name: agent.name,
    email: agent.email,
    teamIDs: agent.teamIds,
    roles: [mapPermissionToRole(agent.permission)],
  }));
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const parseSignature = (payload: IUpdateAgentPayload["signature"], transactionID?: string) => {
  if (!payload || (!payload.text && !payload.richTextObj)) {
    return {};
  }

  if (payload.richTextObj === null) {
    return { signature: { text: payload.text } };
  }

  const safePayload = omit(payload, "attachments");

  return { signature: safePayload, ...(payload.richTextObj && transactionID ? { transactionID } : {}) };
};

function parseUpdateAgentPayload(payload: IUpdateAgentPayload): IKeyAny {
  return {
    ...(payload.signature ? parseSignature(payload.signature, payload.transactionID) : {}),
    ...(payload.avatar && { avatar: payload.avatar }),
    ...(payload.name && { name: payload.name }),
    ...(payload.teamIds && { teamIDs: payload.teamIds }),
    ...(payload.permission && { roles: [mapPermissionToRole(payload.permission)] }),
    ...(payload.flags && { flags: payload.flags }),
    ...(payload.settings && { settings: payload.settings }),
    ...(payload.autoassignment !== undefined && { autoassignment: payload.autoassignment }),
    ...(payload.autoassignmentTeamIDs && { autoassignmentTeamIDs: payload.autoassignmentTeamIDs }),
  };
}

export default class AgentApi extends BaseApi {
  protected readonly prefix = "agents";

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetch = (id: string) => this.get(id);
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchAll = () => this.get("");
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchCurrentAgent = () => this.get("me");
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  createMany = (payload: IBatchCreateAgentsPayload) => this.post("batch", parseBatchCreateAgentsParams(payload));
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateAgent = (id: string, data: IUpdateAgentPayload) => this.patch(id, parseUpdateAgentPayload(data));
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  deleteAgent = (id: string) => this.delete(id);
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  setAgentFlag = (id: string, name: string, value: boolean | string) => this.patch(id, { flags: { [name]: value } });
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  sendPushTest = (agentId: string, deviceId: string) =>
    this.post(`${agentId}/webpushSubscriptions/${deviceId}/sendTestNotification`);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  subscribeAgentDevice = (agentId: string, payload: Record<string, unknown>) =>
    this.post(`${agentId}/webpushSubscriptions`, payload);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  resendVerification = () => this.post("me/resendVerification");
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  resendInvitation = (id: string) => this.post(`${id}/resendInvitation`);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  unsubscribeAgentDevice = (agentId: string, deviceId: string) =>
    this.delete(`${agentId}/webpushSubscriptions/${deviceId}`);
}
